"use client";

import React, { useState, useEffect, useRef } from "react";
import { Customer } from "@/types/models/customer";
import { getCustomers, searchCustomers } from "@/services/customerService";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, Search, Check, User } from "lucide-react";
import { cn } from "@/lib/utils";

interface CustomerSelectorProps {
  label?: string;
  value?: string;
  onValueChange: (customerId: string) => void;
  placeholder?: string;
  error?: string;
  helperText?: string;
  onCustomerSelect?: (customer: CustomerOption | null) => void;
}

interface CustomerOption {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  totalOrders?: number;
  addresses?: any[];
}

export const CustomerSelector: React.FC<CustomerSelectorProps> = ({
  label = "Select Customer",
  value,
  onValueChange,
  placeholder = "Search and select customer",
  error,
  helperText,
  onCustomerSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerOption | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Load initial customers (top 10)
  useEffect(() => {
    loadInitialCustomers();
  }, []);

  // Update selected customer when value prop changes
  useEffect(() => {
    if (value) {
      const customer = customers.find((c) => c.id === value);
      if (customer) {
        setSelectedCustomer(customer);
        if (onCustomerSelect) {
          onCustomerSelect(customer);
        }
      }
    } else {
      setSelectedCustomer(null);
      if (onCustomerSelect) {
        onCustomerSelect(null);
      }
    }
  }, [value, customers]);

  const loadInitialCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomers({ page: 1, limit: 10 });
      if (response.success && response.data) {
        const customerOptions: CustomerOption[] = (response.data as Customer[]).map(
          (customer) => ({
            id: customer.id!,
            name: `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "Unknown",
            email: customer.email,
            phone: customer.phone_number,
            totalOrders: customer.total_orders || 0,
            addresses: customer.customer_addresses || [],
          }),
        );
        setCustomers(customerOptions);
      }
    } catch (error) {
      console.error("Failed to load initial customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const searchCustomersApi = async (query: string) => {
    if (!query.trim()) {
      loadInitialCustomers();
      return;
    }

    try {
      setLoading(true);
      const response = await searchCustomers({
        q: query,
        page: 1,
        page_size: 20,
      });

      if (response.success && response.data) {
        const customerOptions: CustomerOption[] = (response.data as Customer[]).map(
          (customer) => ({
            id: customer.id!,
            name: `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "Unknown",
            email: customer.email,
            phone: customer.phone_number,
            totalOrders: customer.total_orders || 0,
            addresses: customer.customer_addresses || [],
          }),
        );
        setCustomers(customerOptions);
      }
    } catch (error) {
      console.error("Failed to search customers:", error);
      const filtered = customers.filter(
        (customer) =>
          customer.name.toLowerCase().includes(query.toLowerCase()) ||
          customer.email?.toLowerCase().includes(query.toLowerCase()) ||
          customer.phone?.includes(query),
      );
      setCustomers(filtered);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchCustomersApi(query);
    }, 300);
  };

  const handleCustomerSelect = (customer: CustomerOption) => {
    setSelectedCustomer(customer);
    onValueChange(customer.id);
    if (onCustomerSelect) {
      onCustomerSelect(customer);
    }
    setOpen(false);
    setSearchQuery("");
  };

  return (
    <div className="space-y-1">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              className="w-full h-auto min-h-[38px] justify-between bg-transparent hover:bg-transparent px-4 py-2 font-normal"
            >
              <div className="flex-1 text-left">
                {selectedCustomer ? (
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <User size={12} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        {selectedCustomer.name}
                      </p>
                      {selectedCustomer.email && (
                        <p className="text-xs text-muted-foreground">
                          {selectedCustomer.email}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-secondary-background text-foreground border border-border rounded-b-xl shadow-[0_10px_15px_-3px_rgba(0,0,0,0.4),0_4px_6px_-2px_rgba(0,0,0,0.25)] dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.8),0_4px_6px_-2px_rgba(0,0,0,0.6)]"
            align="start"
            style={{ width: "var(--radix-popover-trigger-width)" }}
          >
            <div className="flex items-center border-b border-border px-3 bg-secondary-background">
              <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                placeholder="Search customers..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div
              className="max-h-[300px] overflow-y-auto overflow-x-hidden bg-secondary-background rounded-b-xl"
              style={{ scrollBehavior: "smooth" }}
              onWheel={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="p-1 flex flex-col gap-[5px]">
                {loading ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Loading customers...
                  </div>
                ) : customers.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {searchQuery
                      ? "No customers found."
                      : "No customers available."}
                  </div>
                ) : (
                  customers.map((customer) => {
                    const isSelected = value === customer.id;
                    return (
                      <div
                        key={customer.id}
                        className={cn(
                          "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                          isSelected && "bg-accent text-accent-foreground",
                        )}
                        onClick={() => handleCustomerSelect(customer)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 text-primary",
                            isSelected ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User size={14} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-medium">
                              {customer.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {customer.email && (
                                <>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {customer.email}
                                  </p>
                                  <span className="text-xs text-muted-foreground">
                                    •
                                  </span>
                                </>
                              )}
                              {customer.phone && (
                                <>
                                  <p className="text-xs text-muted-foreground">
                                    {customer.phone}
                                  </p>
                                  <span className="text-xs text-muted-foreground">
                                    •
                                  </span>
                                </>
                              )}
                              <p className="text-xs text-muted-foreground">
                                {customer.totalOrders} order
                                {customer.totalOrders !== 1 ? "s" : ""}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {error && <p className="text-red-500 text-xs">{error}</p>}
      {helperText && (
        <p className="text-muted-foreground text-xs">{helperText}</p>
      )}
    </div>
  );
};

export default CustomerSelector;
